import { useState } from 'react';

import Lottie from 'lottie-react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { useTranslation } from 'react-i18next';

import { Input } from '../../../../../Input/Input';
import loader from '../../../../../../assets/newLoader.json';
import { useAPI } from '../../../../../../services/api.service';
import { usePaymentState } from '../../../../hooks/use-payment-state';
import { useCheckout } from '../../../../../../hooks/use-checkout';
import { ECustomEvents } from '../../../../../../contracts';
import { ClearIcon, CouponIcon } from '../../../../../../assets/icons';

import './styles.scss';
import { sendProductEvent } from '../../../../../../utils/mixpanel-utils';

enum CouponStatus {
  IDLE,
  VALID,
  INVALID,
}

export function CouponField() {
  const { API } = useAPI();
  const { getEventParams } = useCheckout();
  const isPaymentLoading = usePaymentState((state) => state.isPaymentLoading);
  const setPriceBreakdown = usePaymentState((state) => state.setPriceBreakdown);
  const promoCodeMetadata = usePaymentState((state) => state.promoCodeMetadata);
  const setPromoCodeMetadata = usePaymentState((state) => state.setPromoCodeMetadata);
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const [couponValue, setCouponValue] = useState('');
  const [validCouponValue, setValidCouponValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [couponStatus, setCouponStatus] = useState<CouponStatus>(CouponStatus.IDLE);
  const [isCancellingPromo, setIsCancellingPromo] = useState(false);

  const validateCoupon = async () => {
    setIsLoading(true);

    try {
      const couponToValidate = couponValue;
      const priceBreakdown = await API.applyPromoCode(couponToValidate);
      setPromoCodeMetadata({
        name: couponToValidate,
        discountAmount: priceBreakdown.discount,
        discountPercentage: priceBreakdown.discountRatePoints,
      });
      setPriceBreakdown(priceBreakdown);
      setCouponValue('');
      setCouponStatus(CouponStatus.VALID);
      setValidCouponValue(couponToValidate);
      setErrorMessage('');
    } catch (error: any) {
      setErrorMessage(error.response.data.message);
      setCouponStatus(CouponStatus.INVALID);
    } finally {
      setIsLoading(false);
    }
  };

  const removeCoupon = async () => {
    setIsCancellingPromo(true);
    try {
      const promoCodeToRemove = promoCodeMetadata?.name || validCouponValue;

      const eventParams = getEventParams();

      const priceBreakdown = await API.cancelPromoCode();
      setPriceBreakdown(priceBreakdown);
      setValidCouponValue('');
      setCouponStatus(CouponStatus.IDLE);
      setPromoCodeMetadata();

      if (promoCodeToRemove) {
        sendProductEvent(ECustomEvents.PROMO_CODE_REMOVED, {
          ...eventParams,
          promoCodeName: promoCodeToRemove,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsCancellingPromo(false);
    }
  };

  const isCouponValid = couponStatus === CouponStatus.VALID;
  const isCouponInvalid = couponStatus === CouponStatus.INVALID;
  const isApplyButtonDisabled = isLoading || isCouponValid || isPaymentLoading;
  return (
    <Box className="coupon-wrapper">
      <Stack direction="row" alignItems="flex-start" spacing={0.5}>
        <Input
          size="medium"
          error={isCouponInvalid ? errorMessage : ''}
          inputProps={{
            placeholder: t('promoCode'),
            value: couponValue,
            readOnly: isLoading,
            onChange: (e) => setCouponValue(e.target.value),
          }}
        />
        <button className="update-button" onClick={validateCoupon} disabled={isApplyButtonDisabled}>
          <span className={isLoading ? 'button-text-hidden' : ''}>{t('apply')}</span>
          {isLoading && <Lottie className="loading" animationData={loader} />}
        </button>
      </Stack>

      {validCouponValue && (
        <Box className="coupon-chip-wrapper">
          <Chip
            variant="outlined"
            disabled={isCancellingPromo}
            deleteIcon={
              <Box>
                <ClearIcon />
              </Box>
            }
            className="coupon-chip"
            onDelete={removeCoupon}
            label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <CouponIcon />
                <Box className="label-text">{validCouponValue}</Box>
              </Stack>
            }
          />
        </Box>
      )}
    </Box>
  );
}
